import { stringToEnum } from '@snap-types/util/enum';

export enum RunRateType {
  DAYS_PER_LOAD = 'DAYS_PER_LOAD',
  HRS_PER_PC = 'HRS_PER_PC',
  PCS_PER_MIN = 'PCS_PER_MIN',
  HRS_PER_LOAD = 'HRS_PER_LOAD',
  PCS_PER_HR = 'PCS_PER_HR',
  PCS_PER_DAY = 'PCS_PER_DAY',
  MIN_PER_PC = 'MIN_PER_PC',
  DAYS_PER_PC = 'DAYS_PER_PC',
  LOADS_PER_HR = 'LOADS_PER_HR',
  LOADS_PER_DAY = 'LOADS_PER_DAY',
}

export const runRateTypeSchema = stringToEnum(RunRateType);

export enum ScrapYieldType {
  SCRAP = 'SCRAP',
  YIELD = 'YIELD',
}

export const scrapYieldTypeSchema = stringToEnum(ScrapYieldType);

export const scrapYieldTypeLabelMapping = {
  SCRAP: 'Scrap',
  YIELD: 'Yield',
} as const satisfies Record<ScrapYieldType, string>;

export enum SnapQuoteAssignmentSource {
  SYSTEM = 'SYSTEM',
  USER = 'USER',
}

export const snapQuoteAssignmentSourceSchema = stringToEnum(
  SnapQuoteAssignmentSource,
);

export enum QtyPerType {
  START = 'START',
  END = 'END',
}

export const qtyPerTypeSchema = stringToEnum(QtyPerType);

export enum QuoteBuilderVersion {
  V1 = 'V1',
  V2 = 'V2',
}

export const quoteBuilderVersionSchema = stringToEnum(QuoteBuilderVersion);

/**
 * Represents the reason/source of a quote save action in the Quote Builder.
 *
 *  - MANUAL: user-initiated by clicking the "Save Quote" button
 *  - AUTO:   saved automatically by the system after an idle period/timeout (see
 *            docs/features/quote-builder/auto-save.md).
 *  - SYSTEM: saved by the app when certain actions occur that require a save,
 *            such as when the user clicks "Preview PDF" or changes the discount
 *            code.
 */
export enum QuoteSaveSource {
  MANUAL = 'MANUAL',
  AUTO = 'AUTO',
  SYSTEM = 'SYSTEM',
}

export const quoteSaveSourceSchema = stringToEnum(QuoteSaveSource);

export enum PriceStrategy {
  CAPPriceSegmentation = 'CAPPriceSegmentation',
  PriceSegmentationWithTargetGrossMargin = 'PriceSegmentationWithTargetGrossMargin',
  FixedCustomer = 'FixedCustomer',
  FixedDefault = 'FixedDefault',
  FixedDiscount = 'FixedDiscount',
  FixedMarket = 'FixedMarket',
  NewPart = 'NewPart',
  /**
   * Used exclusively by the now-retired Configurator V2 (USA Stud) "third
   * party parts" flow, for parts created without an operation/process
   * selected (`NewPart` was used when one was selected). Retired, along
   * with the rest of Configurator V2, in PR #TBD (commit TBD) — no code
   * path can create a new quote line with this strategy anymore
   * (insert-snap-quote-line.ts rejects it explicitly). Kept only so
   * historic quote lines that already have it keep working.
   */
  NewPartUserCost = 'NewPartUserCost',
  NewPartUserPrice = 'NewPartUserPrice',
  PriceSegmentation = 'PriceSegmentation',
  RAFFixedMarket = 'RAFFixedMarket',
  RAFTargetGrossMargin = 'RAFTargetGrossMargin',
  ServiceCharge = 'ServiceCharge',
  TargetContributionMargin = 'TargetContributionMargin',
  TargetGrossMargin = 'TargetGrossMargin',
  UserImport = 'UserImport',
  UserPrice = 'UserPrice',
  Alpha = 'Alpha',
}

export const priceStrategySchema = stringToEnum(PriceStrategy);

export const PriceStrategiesThatRelyOnUnitCost = [
  PriceStrategy.CAPPriceSegmentation,
  PriceStrategy.PriceSegmentation,
  PriceStrategy.PriceSegmentationWithTargetGrossMargin,
  PriceStrategy.RAFTargetGrossMargin,
  PriceStrategy.TargetContributionMargin,
  PriceStrategy.TargetGrossMargin,
];

export enum LeadTimeStrategy {
  PLANNING_LEADTIME = 'PLANNING_LEADTIME',
  CONSTANT_VALUE = 'CONSTANT_VALUE',
  RAF_FIXED_MARKET = 'RAF_FIXED_MARKET',
  QUANTITY_AWARE = 'QUANTITY_AWARE',
}

export const leadTimeStrategySchema = stringToEnum(LeadTimeStrategy);

export enum CustomerPotentialRanking {
  Low = 'Low',
  Medium = 'Medium',
  High = 'High',
}

export const customerPotentialRankingSchema = stringToEnum(
  CustomerPotentialRanking,
);

export enum ProductComplexityRanking {
  Simple = 'Simple',
  Moderate = 'Moderate',
  Complex = 'Complex',
}

export const productComplexityRankingSchema = stringToEnum(
  ProductComplexityRanking,
);

export enum ConfiguratorSnapshotVersion {
  V1 = 'V1',
  V2 = 'V2',
}

export const configuratorSnapshotVersionSchema = stringToEnum(
  ConfiguratorSnapshotVersion,
);

export enum PartSpecTable {
  PART_BINARY = 'PART_BINARY',
  PART_CO_BINARY = 'PART_CO_BINARY',
}

export const partSpecTableSchema = stringToEnum(PartSpecTable);

export enum LeadTimeFormat {
  BOTH = 'BOTH',
  NUMBER = 'NUMBER',
  TEXT = 'TEXT',
}

export const leadTimeFormatSchema = stringToEnum(LeadTimeFormat);

export enum SnapUnsupportedCalculation {
  OPERATION_RUN_TYPE_UNKNOWN = 'OPERATION_RUN_TYPE_UNKNOWN',
  REQUIREMENT_HAS_SUBASSEMBLY = 'REQUIREMENT_HAS_SUBASSEMBLY',
  REQUIREMENT_USAGE_UOM_HAS_MULTIPLIER = 'REQUIREMENT_USAGE_UOM_HAS_MULTIPLIER',
  PART_HAS_SUB_OPERATIONS = 'PART_HAS_SUB_OPERATIONS',
}

export const requiresUnsupportedCalculationsReasonSchema = stringToEnum(
  SnapUnsupportedCalculation,
);

export enum SnapQuoteStatus {
  INPROGRESS = 'INPROGRESS',
  SENT = 'SENT',
  DELETED = 'DELETED',
}

export const snapQuoteStatusSchema = stringToEnum(SnapQuoteStatus);

// NOTE - See Visual Mapping at @snap-types/models/visual/user-def-fields/index.ts
export enum WonLossStatus {
  COMPLETED = 'COMPLETED',
  CONVERTED = 'CONVERTED',
  LOSS_CAPABILITIES = 'LOSS_CAPABILITIES',
  LOSS_CAPABILITIES_NO_QUOTE = 'LOSS_CAPABILITIES_NO_QUOTE',
  LOSS_CUSTOMER_LOST_BID = 'LOSS_CUSTOMER_LOST_BID',
  LOSS_EXCEPTIONS_NOT_ACCEPTED = 'LOSS_EXCEPTIONS_NOT_ACCEPTED',
  LOSS_LACKING_CERTIFICATION = 'LOSS_LACKING_CERTIFICATION',
  LOSS_LEAD_TIME = 'LOSS_LEAD_TIME',
  LOSS_QUOTE_NOT_FAST_ENOUGH = 'LOSS_QUOTE_NOT_FAST_ENOUGH',
  LOSS_PROJECT_CANCELLED = 'LOSS_PROJECT_CANCELLED',
  LOSS_PRICE = 'LOSS_PRICE',
  LOSS_QUOTING_FOR_MULTIPLE_CUSTOMERS = 'LOSS_QUOTING_FOR_MULTIPLE_CUSTOMERS',
  LOSS_QUOTING_FOR_MULTIPLE_REVS_PART_ITERATIONS = 'LOSS_QUOTING_FOR_MULTIPLE_REVS_PART_ITERATIONS',
  LOSS_ROUTINE_COMPETITIVE_CHECK = 'LOSS_ROUTINE_COMPETITIVE_CHECK',
  LOSS_TERMS_OF_SALE = 'LOSS_TERMS_OF_SALE',
  PAUSED = 'PAUSED',
  PENDING_BUYOUT_PRICE = 'PENDING_BUYOUT_PRICE',
  PENDING_CUSTOMER_RESPONSE = 'PENDING_CUSTOMER_RESPONSE',
  PENDING_MANUFACTURING = 'PENDING_MANUFACTURING',
  PENDING_OUTSIDE_PROCESS = 'PENDING_OUTSIDE_PROCESS',
  PENDING_REVISED_PO_CONTRACT_REVIEW = 'PENDING_REVISED_PO_CONTRACT_REVIEW',
  PENDING_REVISED_PO_PRICE = 'PENDING_REVISED_PO_PRICE',
  QUOTED = 'QUOTED',
  RFQ_RECEIVED = 'RFQ_RECEIVED',
  WIN_CAPABILITIES = 'WIN_CAPABILITIES',
  WIN_LEAD_TIME = 'WIN_LEAD_TIME',
  WIN_PRICE = 'WIN_PRICE',
}

export const wonLossStatusSchema = stringToEnum(WonLossStatus);

export enum UnitsOfMeasure {
  BAR = 'BAR',
  BAR10 = 'BAR10',
  BAR12 = 'BAR12',
  BAR16 = 'BAR16',
  BAR6 = 'BAR6',
  BX = 'BX',
  BX100 = 'BX100',
  BX1000 = 'BX1000',
  BX25 = 'BX25',
  BX50 = 'BX50',
  C = 'C',
  CLB = 'CLB',
  CS = 'CS',
  CTN = 'CTN',
  DAB = 'DAB',
  DZ = 'DZ',
  EA = 'EA',
  FT = 'FT',
  GAL = 'GAL',
  HR = 'HR',
  IN = 'IN',
  K = 'K',
  KG = 'KG',
  LB = 'LB',
  LBC = 'LBC',
  LBM = 'LBM',
  LBS = 'LBS',
  LOT = 'LOT',
  LY15 = 'LY15',
  LY40 = 'LY40',
  LY42 = 'LY42',
  LY43 = 'LY43',
  LY50 = 'LY50',
  M = 'M',
  MLB = 'MLB',
  MM = 'MM',
  OZ = 'OZ',
  PKG = 'PKG',
  PR = 'PR',
  SET = 'SET',
  SQFT = 'SQFT',
  SQIN = 'SQIN',
  TOZ = 'TOZ',
  TUBE8OZ = 'TUBE8OZ',

  UNKNOWN = 'UNKNOWN',
}

export const unitsOfMeasureSchema = stringToEnum(UnitsOfMeasure);

export function convertUnitsOfMeasureToEnum(value: string | null | undefined) {
  if (!value) {
    return null;
  }

  try {
    return unitsOfMeasureSchema.parse(value.toUpperCase());
  } catch {
    return UnitsOfMeasure.UNKNOWN;
  }
}

/** The unit a site quotes and sells in. */
export enum SellingUnitOfMeasure {
  EA = 'EA',
  M = 'M',
}

export const sellingUnitOfMeasureSchema = stringToEnum(SellingUnitOfMeasure);

/**
 * How many pieces one selling unit represents. This is the numeric meaning of
 * the enum rather than a presentation concern, so it lives here alongside it —
 * the label and description maps stay in `@lib/enums/selling-unit-of-measure`.
 */
export const SELLING_UOM_MULTIPLIER = {
  [SellingUnitOfMeasure.EA]: 1,
  [SellingUnitOfMeasure.M]: 1000,
} satisfies Record<SellingUnitOfMeasure, number>;

/**
 * The same conversion counted in decimal places rather than as a factor. A
 * per-piece value carries this many digits more than the same value quoted per
 * selling unit, so a precision expressed in the selling unit shifts by it.
 *
 * Kept as its own map rather than derived from {@link SELLING_UOM_MULTIPLIER}
 * because `Math.log10` on a power of ten is not reliably exact; a test asserts
 * the two agree.
 */
export const SELLING_UOM_DECIMAL_SHIFT = {
  [SellingUnitOfMeasure.EA]: 0,
  [SellingUnitOfMeasure.M]: 3,
} satisfies Record<SellingUnitOfMeasure, number>;

export enum AutoConfiguratorImplementation {
  ALPHA = 'ALPHA',
}

export const autoConfiguratorImplementationSchema = stringToEnum(
  AutoConfiguratorImplementation,
);

export enum AlphaDimensionUnit {
  IMPERIAL = 'IMPERIAL',
  METRIC = 'METRIC',
}

export const alphaDimensionUnitSchema = stringToEnum(AlphaDimensionUnit);

export enum AlphaQuoteLineStatus {
  ACTIVE = 'ACTIVE',
  PENDING_DELETE = 'PENDING_DELETE',
}

export const alphaQuoteLineStatusSchema = stringToEnum(AlphaQuoteLineStatus);

export enum AlphaQuoteEvaluationStatus {
  INCOMPLETE = 'INCOMPLETE',
  CALCULATED = 'CALCULATED',
  CONSULT_ENGINEERING = 'CONSULT_ENGINEERING',
  CALCULATION_ERROR = 'CALCULATION_ERROR',
}

export const alphaQuoteEvaluationStatusSchema = stringToEnum(
  AlphaQuoteEvaluationStatus,
);

export enum AlphaQuoteSyncStatus {
  UNSYNCED = 'UNSYNCED',
  SYNCED = 'SYNCED',
  STALE = 'STALE',
}

export const alphaQuoteSyncStatusSchema = stringToEnum(AlphaQuoteSyncStatus);

export enum SalesTaxGroupId {
  NON_TAXABLE = 'NON_TAXABLE',
}

export const salesTaxGroupIdSchema = stringToEnum(SalesTaxGroupId);

export function convertSalesTaxGroupIdToEnum(value: string | null | undefined) {
  if (!value) {
    return null;
  }

  return salesTaxGroupIdSchema.parse(value.toUpperCase().replace(' ', '_'));
}

export enum SiteCode {
  ADF = 'ADF',
  AMF = 'AMF',
  ANA = 'ANA',
  ASC = 'ASC',
  ASM = 'ASM',
  ATL = 'ATL',
  BTL = 'BTL',
  CAP = 'CAP',
  CP = 'CP',
  CSC = 'CSC',
  ECO = 'ECO',
  EFG = 'EFG',
  ELK = 'ELK',
  EPV = 'EPV',
  ESP = 'ESP',
  GRE = 'GRE',
  GRS = 'GRS',
  HOL = 'HOL',
  HPC = 'HPC',
  HPS = 'HPS',
  LPF = 'LPF',
  LVP = 'LVP',
  MHK = 'MHK',
  MMC = 'MMC',
  MPS = 'MPS',
  MRX = 'MRX',
  MWC = 'MWC',
  MWS = 'MWS',
  NOR = 'NOR',
  NW = 'NW',
  ORP = 'ORP',
  PON = 'PON',
  QBS = 'QBS',
  RAF = 'RAF',
  ROC = 'ROC',
  RUM = 'RUM',
  SPM = 'SPM',
  SSX = 'SSX',
  SVM = 'SVM',
  TEL = 'TEL',
  TSI = 'TSI',
  USA = 'USA',
  WWP = 'WWP',
}

export const siteCodeSchema = stringToEnum(SiteCode);

export enum WorkOrderType {
  QUOTE = 'QUOTE',
  ENGINEERING_MASTER = 'ENGINEERING_MASTER',
  WORK_ORDER = 'WORK_ORDER',
}

export enum WorkOrderStatus {
  CLOSED = 'CLOSED',
  FIRMED = 'FIRMED',
  RELEASED = 'RELEASED',
  UNRELEASED = 'UNRELEASED',
  CANCELLED = 'CANCELLED',
}

export enum QuotePdfType {
  ADF = 'ADF',
  AMF = 'AMF',
  ANA = 'ANA',
  ASM = 'ASM',
  BTL = 'BTL',
  CAP = 'CAP',
  CSC = 'CSC',
  CSC_CUSTOM = 'CSC_CUSTOM',
  DEFAULT = 'DEFAULT',
  GRE = 'GRE',
  HPC = 'HPC',
  MMC = 'MMC',
  MWS = 'MWS',
  MWS_HYPERCO = 'MWS_HYPERCO',
  PON = 'PON',
  RAF = 'RAF',
  ROC = 'ROC',
  SPM = 'SPM',
  USA = 'USA',
  WWP = 'WWP',
}

export type QuotePdfMetadataType = {
  name: string;
  limitedToSites: SiteCode[] | null;
};

export const quotePdfTypeMetadata: Record<QuotePdfType, QuotePdfMetadataType> =
  {
    ADF: {
      name: 'ADF',
      limitedToSites: [SiteCode.ADF],
    },
    AMF: {
      name: 'AMF',
      limitedToSites: [SiteCode.AMF],
    },
    ANA: {
      name: 'ANA',
      limitedToSites: [SiteCode.ANA],
    },
    ASM: {
      name: 'ASM',
      limitedToSites: [SiteCode.ASM],
    },
    BTL: {
      name: 'BTL',
      limitedToSites: [SiteCode.BTL],
    },
    CAP: {
      name: 'CAP',
      limitedToSites: [SiteCode.CAP],
    },
    CSC: {
      name: 'CSC',
      limitedToSites: [SiteCode.CSC],
    },
    CSC_CUSTOM: {
      name: 'CSC - Custom Parts Team',
      limitedToSites: [SiteCode.CSC],
    },
    DEFAULT: {
      name: 'DEFAULT',
      limitedToSites: null,
    },
    GRE: {
      name: 'GRE',
      limitedToSites: [SiteCode.GRE],
    },
    HPC: {
      name: 'HPC',
      limitedToSites: [SiteCode.HPC],
    },
    MMC: {
      name: 'MMC',
      limitedToSites: [SiteCode.MMC],
    },
    MWS: {
      name: 'MWS',
      limitedToSites: [SiteCode.MWS],
    },
    MWS_HYPERCO: {
      name: 'MWS - Hyperco',
      limitedToSites: [SiteCode.MWS],
    },
    PON: {
      name: 'PON',
      limitedToSites: [SiteCode.PON],
    },
    RAF: {
      name: 'RAF',
      limitedToSites: [SiteCode.RAF],
    },
    ROC: {
      name: 'ROC',
      limitedToSites: [SiteCode.ROC],
    },
    SPM: {
      name: 'SPM',
      limitedToSites: [SiteCode.SPM],
    },
    USA: {
      name: 'USA',
      limitedToSites: [SiteCode.USA],
    },
    WWP: {
      name: 'WWP',
      limitedToSites: [SiteCode.WWP],
    },
  };

export const quotePdfTypeSchema = stringToEnum(QuotePdfType);

export enum EmailConfigType {
  ANA = 'ANA',
  CSC = 'CSC',
  CSC_CUSTOM = 'CSC_CUSTOM',
  DEFAULT = 'DEFAULT',
  DO_NOT_SEND = 'DO_NOT_SEND',
  RAF = 'RAF',
  USA = 'USA',
}

export const emailConfigTypeSchema = stringToEnum(EmailConfigType);

type EmailConfigMetadataType = {
  name: string;
  limitedToSites: SiteCode[] | null;
};

export const emailConfigTypeMetadata: Record<
  EmailConfigType,
  EmailConfigMetadataType
> = {
  ANA: {
    name: 'From ANA Address, BCC User',
    limitedToSites: [SiteCode.ANA],
  },
  CSC: {
    name: 'From & BCC CSC Email',
    limitedToSites: [SiteCode.CSC],
  },
  CSC_CUSTOM: {
    name: 'From & BCC CSC Custom Quotes Email',
    limitedToSites: [SiteCode.CSC],
  },
  DEFAULT: {
    name: 'From & BCC SNAP User',
    limitedToSites: null,
  },
  DO_NOT_SEND: {
    name: 'Do Not Send Quote Emails',
    limitedToSites: null,
  },
  RAF: {
    name: 'From User, BCC DEastwood',
    limitedToSites: [SiteCode.RAF],
  },
  USA: {
    name: 'From User, BCC USA Fastener Email',
    limitedToSites: [SiteCode.USA],
  },
};

// Each value must correspond (lowercased) to a directory under
// packages/api/src/emails/customer-quote/templates/
export enum QuoteEmailTemplate {
  CAP = 'CAP',
  CSC_CUSTOM = 'CSC_CUSTOM',
  DEFAULT = 'DEFAULT',
}

export const quoteEmailTemplateSchema = stringToEnum(QuoteEmailTemplate);

type QuoteEmailTemplateMetadataType = {
  name: string;
  limitedToSites: SiteCode[] | null;
};

export const quoteEmailTemplateMetadata: Record<
  QuoteEmailTemplate,
  QuoteEmailTemplateMetadataType
> = {
  CAP: {
    name: 'CAP',
    limitedToSites: [SiteCode.CAP],
  },
  CSC_CUSTOM: {
    name: 'CSC - Custom Parts Team',
    limitedToSites: [SiteCode.CSC],
  },
  DEFAULT: {
    name: 'DEFAULT',
    limitedToSites: null,
  },
};

export enum CustomerArtifactType {
  VISUAL_QUOTE = 'visualQuote',
  SNAP_QUOTE = 'snapQuote',
  ORDER = 'order',
}

export const customerArtifactTypeSchema = stringToEnum(CustomerArtifactType);

export enum AlphaSite {
  ADF = 'ADF',
  ROC = 'ROC',
}

export const alphaSiteSchema = stringToEnum(AlphaSite);

export enum UserRole {
  READ_ONLY = 'READ_ONLY', // Can only view quotes, cannot edit anything other than their own profile
  ASSISTANT = 'ASSISTANT', // Can create Quotes and fill out the Quote level data, but not create Quote Lines or Send
  RESTRICTED = 'RESTRICTED', // Can do everything on the Quoting side but Send Quotes to ERP
  ESTIMATOR = 'ESTIMATOR', // Can Send Quotes to ERP
  SITE_ADMIN = 'SITE_ADMIN', // Can access the Site Admin for the Sites for which they are assigned to Teams
  SUPERADMIN = 'SUPERADMIN', // Can do anything
}

export const userRoleSchema = stringToEnum(UserRole);

export const userRoleLabelMap: Record<UserRole, string> = {
  [UserRole.READ_ONLY]: 'View Only',
  [UserRole.ASSISTANT]: "Assistant (can't add lines)",
  [UserRole.RESTRICTED]: "Restricted (can't send quotes)",
  [UserRole.ESTIMATOR]: 'Estimator',
  [UserRole.SITE_ADMIN]: 'Site Admin',
  [UserRole.SUPERADMIN]: 'Superadmin',
};

export enum QuoteStatus {
  NEW = 'NEW',
  INPROGRESS = 'INPROGRESS',
  SENT = 'SENT',
  WON = 'WON',
  LOSS = 'LOSS',
  DELETED = 'DELETED',
  CANCELLED = 'CANCELLED',
  INHOUSE = 'INHOUSE',
  READY = 'READY',
  TEST = 'TEST',
}

export const quoteStatusSchema = stringToEnum(QuoteStatus);

export enum FeatureVisibility {
  NONE = 'NONE',
  SUPER_ADMIN = 'SUPER_ADMIN',
  ALL = 'ALL',
}

export const featureVisibilitySchema = stringToEnum(FeatureVisibility);

export enum QuoteOrigin {
  WEB = 'WEB',
  SNAP = 'SNAP',
  ALL = 'ALL',
}

export const quoteOriginSchema = stringToEnum(QuoteOrigin);
